"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  HeartPulse,
  Sparkles,
  Target,
  Stethoscope,
  Brain,
  Cpu,
  ArrowRight,
  Crown,
  Mic,
  Layers,
  Activity,
  ShieldCheck,
  TrendingUp,
  BookOpen,
  CheckCircle2,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { PillarGridBlock } from "@/lib/blocks/pillar-grid-block";
import { CardGridBlock } from "@/lib/blocks/card-grid-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const EVIDENCE = [
  { metric: "MBBS", suffix: "", label: "Medical Degree", sub: "Gynaecology specialization, Delhi", accent: "red" },
  { metric: "15", suffix: "+", label: "Years Sovereign-Scale", sub: "Clinical rigor applied to operations", accent: "emerald" },
  { metric: "9", suffix: "", label: "AI Platforms", sub: "Clinical discipline across all", accent: "purple" },
  { metric: "0", suffix: "", label: "Security Incidents", sub: "Medical-grade outcome accountability", accent: "cyan" },
];

const PRINCIPLES = [
  { num: "01", title: "Diagnose Before Treating", body: "The first rule of medicine and the most violated rule of leadership. Most organizations act on symptoms. The disease progresses undetected. The method demands forensic diagnosis before intervention." },
  { num: "02", title: "Triage Under Pressure", body: "When everything is urgent, nothing is solved well. The leaders who fail rarely lack the ability to act. They lack the ability to rank. Triage is sequencing under fear." },
  { num: "03", title: "Run the Protocol; Keep the Judgment", body: "Protocols carry the ninety-five percent. Judgment handles the five percent. Brilliance without a system is unsustainable. A system without judgment cannot adapt." },
  { num: "04", title: "Own the Outcome, Not the Effort", body: "Medicine does not credit a physician for how hard the attempt was. The patient lived or did not. Organizations have lost this accountability. Restore it, and the culture transforms." },
  { num: "05", title: "First, Do No Harm", body: "Under pressure, most leaders make the situation worse. The discipline of not making it worse is the most underrated operational skill. Stabilize before amplifying. Contain before responding." },
  { num: "06", title: "Hire the Talent Others Overlook", body: "The most reliable competitive advantage available is the one the world is mis-pricing. Optimize for the right signals. The talent that emerges is the talent that outperforms." },
];

const TRANSLATION = [
  { title: "Diagnostic Dimension", body: "The insistence on understanding the problem before acting. In clinical practice, this is the history and examination. In technology operations, it is the intelligence gathering and analysis.", accent: "red" },
  { title: "Protocol Dimension", body: "The documentation of proven sequences that produce predictable outcomes. In clinical practice, this is the treatment protocol. In technology operations, it is the six-stage Sovereign Operations Architecture.", accent: "purple" },
  { title: "Accountability Dimension", body: "The ownership of results rather than effort. In clinical practice, this is the patient outcome. In technology operations, it is the verified metric — the 83.3% campaign success rate, the 15 victories, the zero breaches.", accent: "cyan" },
];

const APPLICATION = [
  { icon: Brain, title: "Diagnosis in Intelligence", body: "The intelligence architecture exists to understand the condition before acting. The 89% crisis prediction accuracy is not a technology achievement. It is a diagnostic achievement." },
  { icon: Activity, title: "Triage in Operations", body: "Real-time visibility that enables triage: which platform requires immediate attention, which engagement is approaching critical status. The 95% coordination success rate is a triage metric." },
  { icon: ShieldCheck, title: "Protocol in Architecture", body: "Each platform operates on defined protocols — documented sequences that produce verified outcomes. The feedback loop ensures every engagement makes the next one smarter." },
  { icon: TrendingUp, title: "Outcome Accountability", body: "Every metric is an outcome metric. The organization does not report how hard it worked. It reports what it delivered. The medical standard applied to technology operations." },
];

export default function MedicineMeetsTechnologyPage() {
  return (
    <>
      <PageHero
        icon={HeartPulse}
        overline="Medicine Meets Technology"
        overlineClassName="text-emerald"
        title={
          <>
            The Physician&apos;s Discipline for the <span className="text-gradient-premium">Digital Age.</span>
          </>
        }
        subtitle="Dr. Jyoti Kush operates at a convergence that does not exist elsewhere in the world. A qualified physician with an MBBS and a specialization in Gynaecology from Delhi. Also the Chief Operating Officer of a Digital Conglomerate commanding nine proprietary AI platforms across eighteen countries. The intersection is not a metaphor. It is the operating architecture."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Expertise", href: "/expertise/" }, { label: "Medicine Meets Technology" }]}
        badge="Where clinical rigor meets sovereign-scale systems"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-5`}>
              <Sparkles className="size-3.5 text-emerald" />
              <span className="text-overline text-emerald">The Unique Intersection</span>
            </div>
            <h2 className="text-display-md mb-5">
              A physician who runs technology at sovereign scale — the only one.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The world produces physicians. The world produces technology executives. The world does not produce physicians who co-build and run a Digital Conglomerate commanding nine proprietary AI platforms across eighteen countries. This intersection does not exist elsewhere.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The clinical method — the rigor, protocols, and accountability of medical practice — applied to the technology operations Dr. Jyoti Kush commands at sovereign scale. The discipline of diagnosis before treatment. The protocol of triage under pressure. The accountability of owning outcomes. The restraint of doing no harm.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {EVIDENCE.map((e, i) => {
              const a = ACCENT[e.accent];
              return (
                <motion.div
                  key={e.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
                >
                  <div className={`text-display-sm font-bold ${a.text} leading-none mb-2`}>{e.metric}{e.suffix}</div>
                  <div className="text-caption font-bold uppercase tracking-wider mb-1">{e.label}</div>
                  <div className="text-caption text-muted-foreground">{e.sub}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <Stethoscope className="size-3.5 text-red" />
            <span className="text-overline text-red">The Six Clinical Principles</span>
          </div>
          <h2 className="text-display-md mb-4">The operating system forged in clinical practice.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Six principles, learned in a delivery room, proven across eighteen countries, applied to every engagement Dr. Jyoti Kush oversees.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PRINCIPLES.map((p, i) => {
            const accents = ["red", "purple", "cyan", "emerald", "amber-gold", "denim"];
            const a = ACCENT[accents[i]];
            return (
              <motion.div
                key={p.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className={`text-display-sm font-bold ${a.text} opacity-30 leading-none mb-3`}>{p.num}</div>
                  <h3 className={`text-heading-md mb-3 ${a.text}`}>{p.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{p.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Brain className="size-3.5 text-purple" />
            <span className="text-overline text-purple">The Translation Layer</span>
          </div>
          <h2 className="text-display-md mb-4">From delivery room to boardroom to war room.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The translation from clinical practice to organizational leadership requires a deliberate translation layer. Three dimensions constitute the clinical method as applied to technology operations.
          </p>
        </div>
        <div className="space-y-3">
          {TRANSLATION.map((t, i) => {
            const a = ACCENT[t.accent];
            return (
              <motion.div
                key={t.title}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="grid lg:grid-cols-[auto_1fr] gap-5 items-center">
                  <div className={`size-14 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0 shadow-lg`}>
                    <span className="text-body-md font-bold text-white">D{`0${i + 1}`}</span>
                  </div>
                  <div>
                    <h3 className={`text-heading-md mb-1 ${a.text}`}>{t.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{t.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Activity className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Clinical Method Applied</span>
          </div>
          <h2 className="text-display-md mb-4">Four operational domains where clinical discipline scales.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The six clinical principles are embedded into the operational architecture that Dr. Jyoti Kush commands. Each domain demonstrates the translation.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {APPLICATION.map((a, i) => {
            const accents = ["red", "purple", "cyan", "emerald"];
            const ac = ACCENT[accents[i]];
            return (
              <motion.div
                key={a.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`glass-card rounded-2xl p-6 bg-gradient-to-br ${ac.bg} to-transparent border ${ac.border}`}
              >
                <div className="size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center mb-4 shadow-lg">
                  <a.icon className="size-6 text-white" />
                </div>
                <h3 className={`text-heading-md mb-3 ${ac.text}`}>{a.title}</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed">{a.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20" id="medicine-tech-matrix" ariaLabel="Medicine x Tech Matrix">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Brain className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Medicine x Tech Matrix</span>
          </div>
          <h2 className="text-display-md mb-4">Four quadrants. One unified clinical technology.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The signature 2x2 grid. The intersection of two disciplines — clinical medicine and sovereign technology. Each quadrant represents a domain where the combination produces an outcome neither discipline can achieve alone.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {[
            {
              title: "Diagnostic Intelligence",
              header: "Clinical Diagnosis",
              footer: "Sovereign AI",
              body: "The clinical differential diagnosis method — pattern recognition, hypothesis generation, evidence weighting — translated into the intelligence architecture. The 89% crisis prediction accuracy is not a technology metric. It is a diagnostic metric.",
              accent: "red",
            },
            {
              title: "Protocol Systems",
              header: "Clinical Protocols",
              footer: "Platform Architecture",
              body: "The treatment protocol — the documented sequence that produces a predictable outcome — translated into the platform architecture. Each of the nine proprietary platforms operates on defined protocols refined across sovereign-scale deployments.",
              accent: "purple",
            },
            {
              title: "Patient Outcome Engineering",
              header: "Clinical Outcomes",
              footer: "Verified Metrics",
              body: "The patient outcome — the patient lived or did not — translated into outcome accountability for organizations. Every metric Dr. Jyoti Kush reports is an outcome metric, never an activity metric. The medical standard applied to organizational leadership.",
              accent: "cyan",
            },
            {
              title: "Sovereign Patient Care",
              header: "Clinical Care",
              footer: "Sovereign Operations",
              body: "The continuity of care model — the patient is treated across multiple touchpoints, with full history available at each transition — translated into sovereign operations. The handoff protocol that prevents information loss becomes the operational standard.",
              accent: "emerald",
            },
          ].map((q, i) => {
            const a = ACCENT[q.accent];
            const QuadrantIcon = [Stethoscope, Activity, Target, HeartPulse][i];
            return (
              <motion.div
                key={q.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} overflow-hidden hover:scale-[1.01] transition-all`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`text-overline ${a.text}`}>{q.header}</div>
                    <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shadow-lg`}>
                      <QuadrantIcon className="size-5 text-white" />
                    </div>
                  </div>
                  <h3 className={`text-heading-md mb-2 ${a.text}`}>{q.title}</h3>
                  <div className={`text-caption font-semibold ${a.text} opacity-60 mb-3`}>x {q.footer}</div>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{q.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <Stethoscope className="size-3.5 text-red" />
            <span className="text-overline text-red">What Medicine Systematized</span>
          </div>
          <h2 className="text-display-md mb-4">The mechanisms that translate to organizations.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Medicine systematized excellence centuries before business attempted it. The mechanisms that make medicine reliable are the mechanisms that translate to sovereign operations.
          </p>
        </div>
        <PillarGridBlock
          columns={3}
          variant="card"
          pillars={[
            { title: "The Checklist", body: "The discipline that prevents omission errors. In medicine, the surgical checklist. In technology operations, the engagement checklist. The tool that prevents excellence from degrading under pressure.", icon: <CheckCircle2 className="size-5" />, accent: "red" },
            { title: "The Handoff Protocol", body: "The mechanism that ensures continuity across transitions. In medicine, the patient handoff. In technology operations, the platform-to-platform handoff. The protocol that prevents information loss.", icon: <Activity className="size-5" />, accent: "purple" },
            { title: "The M&M Conference", body: "The ritual where physicians own what went wrong. In medicine, the Morbidity and Mortality conference. In technology operations, the post-engagement review. The discipline that extracts learning from outcomes.", icon: <Target className="size-5" />, accent: "cyan" },
            { title: "The Time-Out", body: "The pause before action that confirms understanding. In medicine, the surgical time-out. In technology operations, the pre-deployment verification. The discipline that prevents misdiagnosis at the most critical moment.", icon: <ShieldCheck className="size-5" />, accent: "emerald" },
            { title: "The Peer Review", body: "The mechanism that maintains standards across the profession. In medicine, the credentialing system. In technology operations, the cross-functional review. The discipline that prevents drift.", icon: <Brain className="size-5" />, accent: "amber-gold" },
            { title: "The Differential", body: "The discipline of generating multiple hypotheses before committing. In medicine, the differential diagnosis. In technology operations, the multi-vector analysis. The discipline that prevents premature commitment.", icon: <Stethoscope className="size-5" />, accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Sparkles className="size-3.5 text-purple" />
            <span className="text-overline text-purple">The Triple Threat Doctrine</span>
          </div>
          <h2 className="text-display-md mb-4">Medicine, technology, politics. One integrated method.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The signature doctrine. Three domains integrated into a single operating method. Each domain reinforces the others. The integration is the capability no single-domain specialist can replicate.
          </p>
        </div>
        <CardGridBlock
          columns={3}
          items={[
            { title: "Medicine as Discipline", body: "The clinical foundation. Diagnostic rigor, protocol adherence, outcome accountability, the discipline of doing no harm. The operating system that produces reliable outcomes under pressure.", icon: <Stethoscope className="size-5" />, accent: "red" },
            { title: "Technology as Infrastructure", body: "Nine proprietary AI/ML platforms. The infrastructure that translates clinical discipline into operational capability. Built in-house over fifteen-plus years. Zero third-party dependencies.", icon: <Cpu className="size-5" />, accent: "purple" },
            { title: "Politics as Stakes", body: "The political domain provides the stakes. Sovereign-scale operations where failure is measured in national stability. The discipline and infrastructure meet the highest-stakes environment on earth.", icon: <Target className="size-5" />, accent: "cyan" },
            { title: "The Integration", body: "Clinical rigor applied to technological systems under political-grade stakes. The integration is the operator. The methodology is the bridge. The outcome is what no single-domain specialist can produce.", icon: <Layers className="size-5" />, accent: "emerald" },
            { title: "The Triple Threat Output", body: "Eighty-three-point-three percent campaign success rate. Fifteen victories in eighteen engagements. Nine hundred million-plus voters influenced. Zero security breaches. The output that the triple threat doctrine produces.", icon: <TrendingUp className="size-5" />, accent: "amber-gold" },
            { title: "The Uniqueness", body: "The world produces physicians. The world produces technology executives. The world does not produce physicians who co-build and run a Digital Conglomerate. The intersection does not exist elsewhere.", icon: <Crown className="size-5" />, accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-emerald/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-red/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Sparkles className="size-10 text-emerald mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Engage the clinical discipline.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              The intersection of medicine and technology produces a form of operational precision that no single-domain specialist can replicate. Keynotes, advisory, and consulting engagements available.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin an Engagement
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/expertise/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Layers className="size-4" />
                All Expertise Domains
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Expertise Hub", description: "The complete map of fifteen expertise domains. One integrated operating system.", href: "/expertise/", icon: HeartPulse, accent: "emerald" },
          { title: "Leadership as Diagnosis", description: "The clinical method applied to organizational leadership. Six principles forged in a delivery room.", href: "/expertise/leadership-as-diagnosis/", icon: Stethoscope, accent: "red" },
          { title: "AI, Technology & The Future", description: "Governing intelligence at sovereign scale. The AI architecture the future demands.", href: "/expertise/ai-technology-future/", icon: Brain, accent: "cyan" },
          { title: "About Dr. Jyoti Kush", description: "The physician who became an operator. A decade of sovereign-scale leadership.", href: "/about/", icon: Crown, accent: "amber-gold" },
        ]}
      />
    </>
  );
}
